import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Grid, Icon } from "@mui/material";
import RoadForm from "./forms/RoadForm";
import OceanForm from "./forms/OceanForm";
import AirForm from "./forms/AirForm";
import DashboardNavbar from "../../../../../examples/Navbars/DashboardNavbar";
import Footer from "../../../../../examples/Footer";
import DashboardLayout from "../../../../../examples/LayoutContainers/DashboardLayout";
import CrudService from "services/cruds-service";
import Box from "@mui/material/Box";
import MDTypography from "../../../../../components/MDTypography";
import ProjectCargoForm from "./forms/ProjectCargoForm";
import { useMaterialUIController } from "context";
import { appConfirm } from "components/AppDialog";
import { ReadOnlyBanner, ReadOnlyFieldset } from "components/ReadOnlyLock";

const TRANSPORT_MODES = [
    {
        value: "Road",
        label: "Road",
        icon: "local_shipping",
        description: "Full and part truckload transport, domestic or cross-border.",
        tags: ["FTL", "LTL", "Intermodal"],
    },
    {
        value: "Ocean",
        label: "Ocean",
        icon: "directions_boat",
        description: "Container shipments with port and door services.",
        tags: ["FCL", "LCL"],
    },
    {
        value: "Air",
        label: "Air",
        icon: "flight",
        description: "Airport-to-airport or door services for urgent cargo.",
        tags: ["Air Freight", "Door Services"],
    },
    // { value: "Multimodal", label: "Multimodal", icon: "sync_alt", ... },
    {
        value: "ProjectCargo",
        label: "Project Cargo",
        icon: "precision_manufacturing",
        description: "Oversized, heavy-lift and breakbulk movements.",
        tags: ["BreakBulk", "OOG", "Multimodal"],
    },
];

const ACCENT = "#1A73E8";

const CreateQuotingTool = () => {
    const location = useLocation();
    const { state } = location; // location zaten var
    const [prefill, setPrefill] = useState(null);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const searchParams = new URLSearchParams(location.search);
    const quotingToolId = searchParams.get("id");

    const [selectedTransportType, setSelectedTransportType] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    // An existing quote opens read-only, like the TMS pages; "Edit" unlocks the form.
    const [readOnlyMode, setReadOnlyMode] = useState(Boolean(quotingToolId));

    useEffect(() => {
        if (quotingToolId) {
            setIsEditing(true);
            fetchQuotingToolDetails(quotingToolId);
        }
        setReadOnlyMode(Boolean(quotingToolId));
    }, [quotingToolId]);

    const fetchQuotingToolDetails = async (id) => {
        try {
            const response = await CrudService.getQuotingToolById(id);
            const quotingToolData = response.data.attributes;
            setSelectedTransportType(quotingToolData.selectedTransportType);
        } catch (error) {
            console.error("Error fetching quoting tool details:", error);
        }
    };
    useEffect(() => {
        // RFQ’dan gelmiş miyiz?
        const incomingType = state?.selectedTransportType || "";
        const incomingPrefill = state?.prefill || null;

        if (incomingType) setSelectedTransportType(incomingType);
        if (incomingPrefill) setPrefill(incomingPrefill);
    }, [state]);

    const changeMode = async (value) => {
        if (value === selectedTransportType) return;
        // Mod değişince açık form sıfırlanır; girilen veriler habersiz kaybolmasın
        if (
            selectedTransportType &&
            !await appConfirm({
                title: "Change transport mode?",
                message: "The data entered in the current form will be cleared. This cannot be undone.",
                confirmText: "Change mode",
                tone: "warning",
            })
        ) {
            return;
        }
        setSelectedTransportType(value);
    };

    const tokens = darkMode
        ? {
            surface: "#131c2e",
            surfaceHover: "#172238",
            border: "rgba(148, 163, 184, 0.16)",
            title: "#f1f5f9",
            text: "#94a3b8",
            tagBg: "rgba(148, 163, 184, 0.10)",
            iconBg: "rgba(26, 115, 232, 0.16)",
            iconColor: "#7cb4ff",
        }
        : {
            surface: "#ffffff",
            surfaceHover: "#f8fafc",
            border: "#e2e8f0",
            title: "#1e293b",
            text: "#64748b",
            tagBg: "#f1f5f9",
            iconBg: "rgba(26, 115, 232, 0.10)",
            iconColor: ACCENT,
        };

    const onCardKeyDown = (e, value) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            changeMode(value);
        }
    };

    // Mod seçilmeden önce: büyük seçim kartları
    const renderModeCards = () => (
        <Box sx={{ width: "100%", maxWidth: 1080, mx: "auto", px: { xs: 1, md: 2 }, pt: { xs: 3, md: 6 }, pb: 6 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
                <MDTypography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ color: ACCENT, letterSpacing: "0.12em", textTransform: "uppercase" }}
                >
                    New Quote
                </MDTypography>
                <MDTypography variant="h4" fontWeight="bold" sx={{ color: tokens.title, mt: 0.5 }}>
                    Create Quote
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" sx={{ color: tokens.text, display: "block", mt: 1 }}>
                    Choose a transport mode to start. You can fill in cargo, routing and pricing details on the next step.
                </MDTypography>
            </Box>

            <Grid container spacing={2.5} role="radiogroup" aria-label="Transport mode">
                {TRANSPORT_MODES.map((mode) => (
                    <Grid item xs={12} sm={6} md={3} key={mode.value}>
                        <Box
                            role="radio"
                            aria-checked={selectedTransportType === mode.value}
                            tabIndex={0}
                            id={`transport-mode-${mode.value}`}
                            onClick={() => changeMode(mode.value)}
                            onKeyDown={(e) => onCardKeyDown(e, mode.value)}
                            sx={{
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                p: 2.5,
                                borderRadius: "12px",
                                border: `1px solid ${tokens.border}`,
                                backgroundColor: tokens.surface,
                                cursor: "pointer",
                                transition: "border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease, background-color 150ms ease",
                                "&:hover": {
                                    borderColor: ACCENT,
                                    backgroundColor: tokens.surfaceHover,
                                    transform: "translateY(-2px)",
                                    boxShadow: darkMode ? "0 8px 24px rgba(0,0,0,0.35)" : "0 8px 24px rgba(15,23,42,0.08)",
                                },
                                "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: "10px",
                                        display: "grid",
                                        placeItems: "center",
                                        backgroundColor: tokens.iconBg,
                                        color: tokens.iconColor,
                                    }}
                                >
                                    <Icon sx={{ fontSize: "24px !important" }}>{mode.icon}</Icon>
                                </Box>
                                <Icon sx={{ color: tokens.text, fontSize: "20px !important", opacity: 0.6 }}>arrow_forward</Icon>
                            </Box>
                            <Box>
                                <MDTypography variant="h6" fontWeight="bold" sx={{ color: tokens.title }}>
                                    {mode.label}
                                </MDTypography>
                                <MDTypography variant="caption" sx={{ color: tokens.text, display: "block", lineHeight: 1.5, mt: 0.5 }}>
                                    {mode.description}
                                </MDTypography>
                            </Box>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: "auto" }}>
                                {mode.tags.map((tag) => (
                                    <Box
                                        key={tag}
                                        component="span"
                                        sx={{
                                            fontSize: "11px",
                                            fontWeight: 500,
                                            lineHeight: 1,
                                            px: 1,
                                            py: 0.6,
                                            borderRadius: "6px",
                                            backgroundColor: tokens.tagBg,
                                            color: tokens.text,
                                        }}
                                    >
                                        {tag}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    // Mod seçildikten sonra: formun üstünde kompakt mod değiştirici
    const renderModeSwitcher = () => (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center", px: { xs: 1, md: 2 }, pt: 2 }}>
            <Box
                role="radiogroup"
                aria-label="Transport mode"
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 0.5,
                    p: 0.5,
                    borderRadius: "10px",
                    border: `1px solid ${tokens.border}`,
                    backgroundColor: tokens.surface,
                }}
            >
                <MDTypography variant="caption" fontWeight="medium" sx={{ color: tokens.text, px: 1.25 }}>
                    Transport mode
                </MDTypography>
                {TRANSPORT_MODES.map((mode) => {
                    const active = selectedTransportType === mode.value;
                    return (
                        <Box
                            key={mode.value}
                            role="radio"
                            aria-checked={active}
                            tabIndex={0}
                            id={`transport-mode-${mode.value}`}
                            onClick={() => changeMode(mode.value)}
                            onKeyDown={(e) => onCardKeyDown(e, mode.value)}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                                px: 1.5,
                                py: 0.75,
                                borderRadius: "8px",
                                cursor: active ? "default" : "pointer",
                                fontSize: "13px",
                                fontWeight: active ? 600 : 500,
                                color: active ? "#ffffff" : tokens.title,
                                backgroundColor: active ? ACCENT : "transparent",
                                transition: "background-color 150ms ease",
                                "&:hover": { backgroundColor: active ? ACCENT : tokens.tagBg },
                                "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
                            }}
                        >
                            <Icon sx={{ fontSize: "18px !important" }}>{mode.icon}</Icon>
                            {mode.label}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {!isEditing && (selectedTransportType ? renderModeSwitcher() : renderModeCards())}
            {readOnlyMode && (
                <Box sx={{ px: { xs: 1, md: 2 }, pt: 2 }}>
                    <ReadOnlyBanner
                        message="This quote is in read-only view. Click Edit to make changes."
                        onEdit={() => setReadOnlyMode(false)}
                        mb={0}
                    />
                </Box>
            )}
            <ReadOnlyFieldset readOnly={readOnlyMode}>
            <Grid container spacing={0} justifyContent="flex-start" sx={{ width: "100%", m: 0, p: 0 }}>
                <Grid item xs={12} sx={{
                    width: "100%",
                    maxWidth: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    ml: 0,
                    pl: 0
                }}>
                    {selectedTransportType === "Road" && (
                        <RoadForm selectedTransportType={selectedTransportType} prefill={prefill} />
                    )}
                    {selectedTransportType === "Ocean" && (
                        <OceanForm selectedTransportType={selectedTransportType} prefill={prefill} />
                    )}
                    {selectedTransportType === "Air" && (
                        <AirForm selectedTransportType={selectedTransportType} prefill={prefill} />
                    )}
                    {selectedTransportType === "ProjectCargo" && (
                        <ProjectCargoForm selectedTransportType={selectedTransportType} prefill={prefill} />
                    )}

                </Grid>
            </Grid>
            </ReadOnlyFieldset>
            <Box sx={{ mt: selectedTransportType ? 0 : { xs: 4, md: 12 } }}>
                <Footer />
            </Box>
        </DashboardLayout>
    );
};

export default CreateQuotingTool;
